import React, { useEffect, useMemo, useState } from "react";
import {
  NavLink,
  Outlet,
  useNavigate,
  useParams,
  useOutletContext,
} from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import {
  FaUsers,
  FaMale,
  FaFemale,
  FaPlus,
  FaLock,
  FaRegCalendarAlt,
  FaImages,
  FaCheckCircle,
  FaQrcode,
  FaPalette,
  FaEye,
  FaCreditCard,
  FaMusic
} from "react-icons/fa";
import {
  MdOutlineTableBar,
  MdPendingActions,
  MdChangeCircle,
  MdLiquor,
  MdCalculate,
} from "react-icons/md";
import { IoBanOutline } from "react-icons/io5";
import { HiMenuAlt2 } from "react-icons/hi";
import { useAuth } from "../Context/AuthContext";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";
import "./AdminLayout.scss";

const TRIAL_LOCK_DAYS = 7;

// Set false to hide Billing & Payments from sidebar
const SHOW_BILLING_PAYMENTS = true;

const AdminLayout = () => {
  const { logout } = useAuth();
  const { slug } = useParams();
  const { weddingData } = useOutletContext();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  const bride_name = weddingData?.bride_name || "Bride";
  const groom_name = weddingData?.groom_name || "Groom";

  const addons = weddingData?.addons || {};
  const isPremium = weddingData?.plan === "premium";

  const toDateObject = (value) => {
    if (!value) return null;

    try {
      if (value?.toDate) return value.toDate();

      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return parsed;

      return null;
    } catch {
      return null;
    }
  };

  const addDays = (dateValue, days) => {
    const date = toDateObject(dateValue);
    if (!date) return null;

    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    result.setDate(result.getDate() + days);
    return result;
  };

  const normalizeDateOnly = (dateValue) => {
    const date = toDateObject(dateValue);
    if (!date) return null;

    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  };

  const lockDate = useMemo(() => {
    if (!weddingData) return null;

    if (isPremium) {
      return normalizeDateOnly(weddingData?.wedding_date);
    }

    return addDays(weddingData?.createdAt, TRIAL_LOCK_DAYS);
  }, [weddingData, isPremium]);

  const isAccountLocked = useMemo(() => {
    if (!lockDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today >= lockDate;
  }, [lockDate]);

  const lockDateText = lockDate ? lockDate.toLocaleDateString() : "—";

  const seatingArrangementEnabled = !isPremium || addons.seatingArrangement;
  const budgetCalculationEnabled = !isPremium || addons.budgetCalculation;
  const qrCodeEnabled = isPremium && addons.qrCode;
  const digitalInviteEnabled = !isPremium || addons.digitalInvite;
  const musicChangeEnabled = isPremium && addons.musicChange;

  useEffect(() => {
    let isMounted = true;

    const loadTheme = async () => {
      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);
        const siteSlug = slug || weddingData?.slug || "";

        if (!siteSlug) {
          if (isMounted) setSiteTheme(fallbackTheme);
          return;
        }

        const siteSnap = await getDoc(doc(db, "sites", siteSlug));

        if (!isMounted) return;

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading admin layout theme:", error);

        if (isMounted) {
          setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
        }
      } finally {
        if (isMounted) {
          setThemeLoading(false);
        }
      }
    };

    loadTheme();

    return () => {
      isMounted = false;
    };
  }, [weddingData, slug]);

  const themeStyle = useMemo(
    () => createThemeStyle(siteTheme || DEFAULT_THEME),
    [siteTheme]
  );

  useEffect(() => {
    const isMobile = window.innerWidth <= 980;

    if (sidebarOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  const navItems = useMemo(() => {
    const items = [
      {
        to: `/sites/${slug}/dashboard`,
        label: "Overview",
        icon: <FaUsers />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/addguest`,
        label: "Add Guests",
        icon: <FaPlus />,
        isLocked: isAccountLocked || !digitalInviteEnabled,
      },
      {
        to: `/sites/${slug}/groom-list`,
        label: `${groom_name}'s Guests`,
        icon: <FaMale />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/bride-list`,
        label: `${bride_name}'s Guests`,
        icon: <FaFemale />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/edit-agenda`,
        label: "Edit My Agenda",
        icon: <FaRegCalendarAlt />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/image-gallery`,
        label: "Image Gallery",
        icon: <FaImages />,
        isLocked: isAccountLocked,
      },

      {
        to: `/sites/${slug}/change-music`,
        label: "Change Music",
        icon: <FaMusic />,
        isLocked: isAccountLocked || !musicChangeEnabled,
      },
      {
        to: `/sites/${slug}/site-colors`,
        label: "Site Colors",
        icon: <FaPalette />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/show-hide-sections`,
        label: "Show / Hide Sections",
        icon: <FaEye />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/addallguests`,
        label: "Assign Tables",
        icon: <MdOutlineTableBar />,
        isLocked: isAccountLocked || !seatingArrangementEnabled,
      },
      {
        to: `/sites/${slug}/budgetcalculation`,
        label: "Budget Calculation",
        icon: <MdCalculate />,
        isLocked: isAccountLocked || !budgetCalculationEnabled,
      },
      {
        to: `/sites/${slug}/participationconfirmed`,
        label: "Participation Confirmed",
        icon: <FaCheckCircle />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/pendingguests`,
        label: "Pending Guests",
        icon: <MdPendingActions />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/notcomingguests`,
        label: "Not Participating",
        icon: <IoBanOutline />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/changegueststate`,
        label: "Change Guest State",
        icon: <MdChangeCircle />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/liquorcount`,
        label: "Liquor Counts",
        icon: <MdLiquor />,
        isLocked: isAccountLocked,
      },
      {
        to: `/sites/${slug}/qrcode`,
        label: "QR Code",
        icon: <FaQrcode />,
        isLocked: isAccountLocked || !qrCodeEnabled,
      },
    ];

    if (SHOW_BILLING_PAYMENTS) {
      items.push({
        to: `/sites/${slug}/billing-payments`,
        label: "Billing & Payments",
        icon: <FaCreditCard />,
        isLocked: false,
      });
    }

    return items;
  }, [
    slug,
    bride_name,
    groom_name,
    seatingArrangementEnabled,
    budgetCalculationEnabled,
    qrCodeEnabled,
    digitalInviteEnabled,
    musicChangeEnabled,
    isAccountLocked,
  ]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      alert("Failed to logout. Please try again.");
    }
  };

  const handleGoToWeddingWebsite = () => {
    window.open(`/sites/${slug}`, "_blank");
  };

  const handleNavItemClick = () => {
    setSidebarOpen(false);
  };

  if (themeLoading || !siteTheme) return null;

  return (
    <div className="adminLayout" style={themeStyle}>
      <aside className={`adminSidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="adminSidebar__inner">
          <div className="adminSidebar__top">
            <div>
              <h1 className="adminSidebar__brand">
                {bride_name} <span>&</span> {groom_name}
              </h1>
              <p className="adminSidebar__sub">Guest Management System</p>
            </div>

            <button
              type="button"
              className="adminSidebar__close"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              ×
            </button>
          </div>

          {isAccountLocked && (
            <div className="adminSidebar__lockNotice">
              This account is locked.
              <br />
              Lock date: <strong>{lockDateText}</strong>
            </div>
          )}

          <nav className="adminSidebar__nav">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `adminSidebar__link ${item.isLocked ? "adminSidebar__link--locked" : ""
                  } ${isActive ? "is-active" : ""}`
                }
                onClick={handleNavItemClick}
              >
                <span className="adminSidebar__icon">{item.icon}</span>
                <span className="adminSidebar__label">{item.label}</span>

                {item.isLocked && (
                  <span className="adminSidebar__lock">
                    <FaLock />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="adminSidebar__bottom">
            <ButtonComponent onClick={handleLogout}>Logout</ButtonComponent>
          </div>
        </div>
      </aside>

      <div
        className={`adminLayout__overlay ${sidebarOpen ? "is-open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      <main className="adminMain">
        <header className="adminTopbar">
          <button
            type="button"
            className="adminTopbar__menu"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
          >
            <HiMenuAlt2 />
          </button>

          <ButtonComponent onClick={handleGoToWeddingWebsite}>
            Go to my wedding website
          </ButtonComponent>
        </header>

        <section className="adminMain__content">
          {isAccountLocked ? (
            <div className="adminMain__lockedCard">
              <div className="adminMain__lockedIcon">
                <FaLock />
              </div>

              <h2 className="adminMain__lockedTitle">Account Locked</h2>

              <p className="adminMain__lockedText">
                {isPremium
                  ? "All premium features are locked from the wedding date."
                  : "Trial features are locked 7 days after the registered date."}
              </p>

              <p className="adminMain__lockedDate">
                Lock date: <strong>{lockDateText}</strong>
              </p>
            </div>
          ) : (
            <Outlet context={{ slug, weddingData }} />
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminLayout;